/**
 * Cookie consent management utilities for eclatinstitute.sg.
 *
 * Reads and writes a first-party `eclat_consent` cookie that stores the
 * visitor's consent preferences. The cookie value is a base-64 encoded JSON
 * object.
 *
 * Consent model: **notice + opt-out** (PDPA business improvement exception).
 * Analytics (GA4 + Meta Pixel) loads by default on English documents; visitors may opt out via
 * the cookie banner. Mandarin documents wait for an explicit analytics choice.
 * Service reliability telemetry (Sentry and Speed
 * Insights) is always-on operational monitoring and is not presented as an
 * optional category.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ConsentPreferences {
  /** Consent storage cookie - always true. */
  necessary: true;
  /** Google Analytics 4 + Meta Pixel - opt-in required. */
  analytics: boolean;
}

export type ConsentCategory = keyof ConsentPreferences;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const CONSENT_COOKIE_NAME = "eclat_consent";
export const CONSENT_SESSION_COOKIE_NAME = "eclat_consent_session";
export const CONSENT_GRANTED_EVENT = "consent-granted";
export const CONSENT_CHANGED_EVENT = "eclat-consent-changed";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year

export const DEFAULT_PREFERENCES: ConsentPreferences = {
  necessary: true,
  analytics: true,
};

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

/**
 * Parse the consent cookie. Returns `null` if absent or malformed -
 * meaning the visitor has not yet interacted with the consent banner.
 */
export function getConsentPreferences(): ConsentPreferences | null {
  if (!isBrowser()) return null;

  const raw = readCookie(CONSENT_COOKIE_NAME);
  if (!raw) return null;

  try {
    const decoded = JSON.parse(
      atob(decodeURIComponent(raw))
    ) as Partial<ConsentPreferences>;
    return normalizeConsentPreferences(decoded);
  } catch {
    return null;
  }
}

export function normalizeConsentPreferences(
  prefs?: Partial<ConsentPreferences> | null
): ConsentPreferences {
  return {
    necessary: true,
    analytics: prefs?.analytics === true,
  };
}

/**
 * Returns `true` when the visitor has not opted out of the given category.
 * English analytics loads by default (opt-out model under PDPA); returns `false`
 * only when the visitor has explicitly rejected the category. Mandarin documents
 * require a stored analytics choice before optional scripts load.
 */
export function hasConsent(category: ConsentCategory): boolean {
  if (category === "necessary") return true;
  const prefs = getConsentPreferences();
  if (!prefs) {
    // Published Mandarin documents require an explicit analytics choice.
    if (isBrowser() && document.documentElement.lang === "zh-Hans")
      return false;
    return DEFAULT_PREFERENCES[category];
  }
  return prefs[category] === true;
}

/**
 * Returns `true` when the visitor has already interacted with the banner.
 */
export function hasConsentResponse(): boolean {
  return getConsentPreferences() !== null;
}

// ---------------------------------------------------------------------------
// Write
// ---------------------------------------------------------------------------

/**
 * Persist consent preferences to a first-party cookie.
 * Also fires `gtag('consent', 'update', ...)` if the Google tag is loaded,
 * and dispatches a custom `eclat-consent-changed` event so other components
 * can react (e.g. ConditionalMetaPixel, LateGA).
 */
export function setConsentPreferences(
  prefs: Omit<ConsentPreferences, "necessary">
): void {
  if (!isBrowser()) return;

  const full = normalizeConsentPreferences(prefs);
  const encoded = encodeURIComponent(btoa(JSON.stringify(full)));

  writeCookie(CONSENT_COOKIE_NAME, encoded, COOKIE_MAX_AGE_SECONDS);
  ensureConsentSessionId();

  try {
    // Sync with Google Consent Mode v2 if gtag is loaded.
    if (typeof window !== "undefined" && (window as { gtag?: unknown }).gtag) {
      (
        window as {
          gtag: (
            cmd: string,
            action: string,
            params: Record<string, string>
          ) => void;
        }
      ).gtag("consent", "update", {
        analytics_storage: full.analytics ? "granted" : "denied",
        ad_storage: full.analytics ? "granted" : "denied",
      });
    }
  } catch {
    // A blocked vendor SDK must not prevent local revocation or its listeners.
  }

  window.dispatchEvent(
    new CustomEvent(CONSENT_CHANGED_EVENT, { detail: full })
  );

  if (full.analytics) {
    window.dispatchEvent(new Event(CONSENT_GRANTED_EVENT));
  }
}

export function acceptAllConsent(): void {
  setConsentPreferences({ analytics: true });
}

export function rejectAllConsent(): void {
  setConsentPreferences({ analytics: false });
}

export function getConsentSessionId(): string | null {
  if (!isBrowser()) return null;
  return readCookie(CONSENT_SESSION_COOKIE_NAME);
}

export function ensureConsentSessionId(): string {
  const existing = getConsentSessionId();
  if (existing) return existing;

  const nextId = crypto.randomUUID();
  writeCookie(CONSENT_SESSION_COOKIE_NAME, nextId, COOKIE_MAX_AGE_SECONDS);
  return nextId;
}

// ---------------------------------------------------------------------------
// Cookie helpers
// ---------------------------------------------------------------------------

function readCookie(name: string): string | null {
  if (!isBrowser()) return null;

  const raw = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));

  if (!raw) return null;
  return raw.slice(name.length + 1);
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  const parts = [
    `${name}=${value}`,
    "path=/",
    `max-age=${maxAgeSeconds}`,
    "SameSite=Lax",
  ];

  if (window.location.protocol === "https:") {
    parts.push("Secure");
  }

  document.cookie = parts.join("; ");
}
